import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

// Enhanced protected route component with role-based access control
const ProtectedRoute = ({ children, requiredRole = null, requiredPermission = null }) => {
  const { isAuthenticated, user, hasPermission } = useAuth();
  const location = useLocation();

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirement
  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check permission requirement
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Super Admin only route protection
export const SuperAdminRoute = ({ children }) => {
  const { changeAccentColor } = useTheme();

  React.useEffect(() => {
    changeAccentColor('#f97316');
  }, []);

  return (
    <ProtectedRoute requiredRole="super_admin">
      {children}
    </ProtectedRoute>
  );
};

// Gym Owner or Super Admin route protection
export const GymOwnerRoute = ({ children }) => {
  const { isSuperAdmin, isGymOwner, user } = useAuth();
  const { changeAccentColor } = useTheme();
  
  React.useEffect(() => {
    if (isSuperAdmin) {
      changeAccentColor('#f97316');
    } else if (isGymOwner && user?.gym_id) {
      const gymColor = localStorage.getItem(`gym_accent_color_${user.gym_id}`);
      if (gymColor) {
        changeAccentColor(gymColor, user.gym_id);
      }
    }
  }, [isSuperAdmin, isGymOwner, user?.gym_id]);

  if (!isSuperAdmin && !isGymOwner) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Permission-based route protection
export const PermissionRoute = ({ children, permission }) => {
  return (
    <ProtectedRoute requiredPermission={permission}>
      {children}
    </ProtectedRoute>
  );
};

// Member only route protection
export const MemberRoute = ({ children }) => {
  const { user } = useAuth();
  const { changeAccentColor } = useTheme();

  React.useEffect(() => {
    if (user?.gym_id) {
      const gymColor = localStorage.getItem(`gym_accent_color_${user.gym_id}`);
      if (gymColor) {
        changeAccentColor(gymColor, user.gym_id);
      }
    }
  }, [user?.gym_id]);

  return (
    <ProtectedRoute requiredRole="member">
      {children}
    </ProtectedRoute>
  );
};

// Role-based component renderer
export const RoleBasedComponent = ({ 
  children, 
  allowedRoles = [], 
  requiredPermission = null,
  fallback = null 
}) => {
  const { user, hasPermission } = useAuth();

  // Check role access
  const hasRoleAccess = allowedRoles.length === 0 || allowedRoles.includes(user?.role);
  
  // Check permission access
  const hasPermissionAccess = !requiredPermission || hasPermission(requiredPermission);

  if (hasRoleAccess && hasPermissionAccess) {
    return children;
  }

  return fallback;
};

export default ProtectedRoute;